import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, Inject } from '@angular/core';

import * as Tokens from '@shared/core/tokens';
import * as Services from '@shared/core/services';
import * as State from '@shared/state';
import { Controllers } from '@shared/core/controllers';

import { DrawCollapseService } from '@shared/core/directives/drawCollapse';

import { Observable, defer, timer, of, merge, fromEvent, BehaviorSubject } from 'rxjs';
import { take, pairwise, switchMap, map, distinctUntilChanged, auditTime, startWith, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';

@Component({
    template: '<div>This component requires extending</div>',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PickupComponent implements OnInit, OnDestroy {
    public futureOrders: boolean = false;

    @Input() public size: OLO.Components.BUTTON_SIZE;
    @Input() public preHead: string = 'GOOD MORNING';
    @Input() public title: string = 'When would you like to pick up your\n order?';
    @Input() public mode: OLO.Components.PICKUP_MODE = 'filter';
    @Input() public labelTitle: string = 'PICKING UP:';
    @Input() public asapTitle: string = 'ASAP';
    @Input() public laterTitle: string = 'Later today';
    @Input() public nextIcon: string = 'next';
    @Input() public dropdownWidth: OLO.Components.DROPDOWN_WIDTH = 'sm';
    @Input() public locationNo!: number;
    @Input() public modalId!: number;
    @Input() public disabled: boolean = false;

    public options$: Observable<OLO.Components.DropDown.IDropDownOption[]>;
    public filters$: Observable<State.ILocationsFilters>;
    public selected$: Observable<OLO.Ordering.IPickupTime>;
    public isHiding$: Observable<boolean>;
    public currentMode$: Observable<OLO.Components.DropDown.IDropDownOption>;
    public isBelowDrawerMediaBreakpoint$: Observable<boolean>;
    public hasLocations$: Observable<boolean>;
    public periods$!: Observable<OLO.Ordering.IPeriod[]>;
    public periodsFiltered$!: Observable<OLO.Ordering.IPeriod[]>;

    public currentLocationPickupTimesList$: Observable<OLO.Ordering.IPickupTime[]>; /* For pickup time selection list */
    public currentLocationPickupTimesListFiltered$: Observable<OLO.Ordering.IPickupTime[]>;

    public currentLocationPickupTimeLabel$: Observable<string>;
    public currentLocationPickupTimeSelected$: Observable<OLO.Ordering.IPickupTime>;

    public cartPickupTimeSelected$: Observable<OLO.Ordering.IPickupTime>;
    public cartPickupTimeSelectedLabel$: Observable<string>;

    // public collectionTypesLinks$: Observable<Models.CollectionTypeItem[]>;
    public isDineInActive$: Observable<boolean>;
    public dropdownToggled$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    constructor(
        @Inject(Tokens.CONFIG_TOKEN) public config: IConfig,
        public controllers: Controllers,
        public drawCollapseService: DrawCollapseService,
        public locationsService: Services.LocationsService,
        public routeService: Services.RouteService,
        public pickupsService: Services.PickupsService,
        public store: Store<State.IStateShared>,
    ) { }

    ngOnInit(): void {
        this.isDineInActive$ = this.controllers.collectionType.isDineInActive$();
        this.futureOrders = this.config.futureOrders === true;
        this.periods$ = this.locationNo ?
            this.controllers.locations.getPeriodsForLocation$(this.locationNo) :
            this.controllers.locations.getPeriodsForAllLocations$();
        this.periodsFiltered$ = this._calculateLaterTimes(this.periods$);
        this.options$ = this.controllers.filters.pickupTimesListForAllLocations$(false, this.futureOrders ? 3 : null, this.futureOrders);
        this.filters$ = this.controllers.filters.getLocationFilters$();
        this.selected$ = this.mode === 'checkout' ? this.controllers.cart.cartPickupTime$() : this.controllers.filters.getLocationFilterSelectedPickupTime$();
        this.currentMode$ = this.controllers.filters.getLocationFilterCurrentMode$();
        this.currentLocationPickupTimesList$ = this.pickupsService.availablePickupsListForLocation$(this.locationNo, this.futureOrders ? 10 : null, this.futureOrders);
        this.currentLocationPickupTimeLabel$ = this.pickupsService.selectedCurrentPickupTimeLabel$(this.futureOrders, this.pickupLabelType);
        this.currentLocationPickupTimeSelected$ = this.pickupsService.selectedCurrentPickupTime$();
        this.cartPickupTimeSelected$ = this.controllers.cart.cartPickupTime$();
        this.cartPickupTimeSelectedLabel$ = this.controllers.cart.cartPickupTimeLabelFull$(null, 'checkoutBox');
        this.hasLocations$ = this.controllers.locations.hasLocations$();

        /* This will prevent from animating later field when going from selected pickup time to asap */
        this.isHiding$ = this.filters$
            .pipe(
                pairwise(),
                switchMap(([prev, next]) => defer(() => {
                    const isRestoring = prev.pickupMode.Id === 2 && prev.pickupTime !== null && next.pickupMode.Id === 1 && next.pickupTime === null;

                    if (isRestoring) {
                        return merge(
                            of(true),
                            timer(500)
                                .pipe(
                                    map(() => false)
                                )
                        );
                    }

                    return of(isRestoring);
                })),
                distinctUntilChanged()
            );

        this.isBelowDrawerMediaBreakpoint$ = fromEvent(window, 'resize')
            .pipe(
                startWith(null),
                auditTime(200),
                map(() => window.innerWidth < this.drawCollapseService.mediaBreakPoint),
            );
        if (new Date().getHours() >= 12 && this.preHead === 'GOOD MORNING') {
            this.preHead = 'GOOD AFTERNOON';
        }
        this.currentLocationPickupTimesListFiltered$ = this.dropdownToggled$.pipe(
            filter(isOpen => isOpen),
            switchMap(() => this._calculateTimesDropdown(this.currentLocationPickupTimesList$, this.futureOrders))
        );
    }

    ngOnDestroy(): void {

    }

    public onLabelClick($event: Event): void {
        this.controllers.filters.getLocationFilters$()
            .pipe(
                take(1)
            )
            .subscribe(filters => {
                if (filters.pickupMode.Id !== 2) {
                    /* Set mode to 'Later today' */
                    this.controllers.filters.onLocationsFilterModeChange(1);
                }
            });
    }

    public onNextClick($event: Event): void {
        /* Just redirect click to label */
        this.onLabelClick($event);
    }

    public onAsapClick($event: Event): void {
        this.controllers.filters.clearPickupFilters();
    }

    public dropdownSelected(pickupTime: OLO.Ordering.IPickupTime, update: boolean = false): void {
        switch (true) {
            case this.mode === 'hero' || this.mode === 'filter' || (this.mode === 'schedule' && !!this.locationNo === false):
                if (pickupTime.Id === -1) {
                    return this.controllers.filters.openScheduleModal(this.modalId || null);
                }

                this.controllers.routes.navigateToLocationsSearchView()
                    .then(() => {
                        this.controllers.filters.selectPickupTimeOption(pickupTime, !this.futureOrders);
                    });

                return;

            case this.mode === 'checkout' || (this.mode === 'schedule' && !!this.locationNo === true && update === true):
                if (pickupTime.Id === -1) {
                    this.controllers.cart.getCartLocationNo$()
                        .pipe(
                            take(1)
                        )
                        .subscribe(locationNo => this.controllers.filters.openScheduleModal(this.modalId || null, locationNo, true));

                    return;
                }

                this.controllers.cart.updateCartPickupTime(pickupTime, this.modalId);

                return;

                // case this.mode === 'current-location' || (this.mode === 'schedule' && !!this.locationNo === true && update === false):
                //     if (pickupTime.Id === -1) {
                //         this.controllers.locations.currentLocation$()
                //             .pipe(
                //                 take(1)
                //             ).subscribe(locationNo => {
                //                 return this.controllers.filters.openScheduleModal(this.modalId || null, locationNo.LocationNo);
                //             });
                //         return;
                //     }
                //     this.pickupsService.selectPickupTimeForCurrentLocation(pickupTime);

                //     return;

            default:
                if (pickupTime.Id === -1) {
                    this.controllers.locations.currentLocation$()
                        .pipe(
                            take(1)
                        )
                        .subscribe(locationNo => this.controllers.filters.openScheduleModal(this.modalId || null, locationNo.LocationNo));

                    return;
                }
                this.controllers.cart.cartTotalQuantity$()
                    .pipe(
                        take(1)
                    )
                    .subscribe(qty => {
                        if (!qty) return this.pickupsService.selectPickupTimeForCurrentLocation(pickupTime);
                        this.controllers.cart.updateCartPickupTime(pickupTime);
                    });

            // console.warn(`pickup.shared.component dropdownSelected mode '${this.mode}' not supported`);
        }

    }

    public get pickupLabelType(): OLO.Types.PICKUP_LABEL_TYPES {
        switch (this.mode) {
            case 'current-location':
                return 'location';
            case 'checkout':
                return 'checkoutBox';
            case 'filter':
            default:
                return 'filter';

        }
    }

    public dropdownToggle(isOpen: boolean): void {
        this.dropdownToggled$.next(isOpen);
        switch (true) {
            case this.mode === 'hero' || this.mode === 'filter':
                if (!isOpen) return;

                this.currentMode$
                    .pipe(
                        take(1)
                    ).subscribe(mode => {
                        if (mode.Id === 2) return;

                        this.controllers.filters.onLocationsFilterModeChange(1);
                    });

                return;

            case this.mode === 'current-location':

                return;

            default:
            // console.warn(`pickup.shared.component dropdownToggle mode '${this.mode}' not supported`);
        }
    }

    public get isDisabled(): boolean {
        return this.disabled;
    }

    private _calculateLaterTimes(PickupTimesList$: Observable<OLO.Ordering.IPeriod[]>): Observable<OLO.Ordering.IPeriod[]> {
        return PickupTimesList$.pipe(
            map(pickupTimes => [...pickupTimes
                .filter(time => time.Id < 0 || (new Date(time.Date).getTime()) > (new Date()).getTime())]));
    }

    private _calculateTimesDropdown(PickupTimesList$: Observable<OLO.Ordering.IPickupTime[]>, futureOrders: boolean): Observable<OLO.Ordering.IPickupTime[]> {
        if (!futureOrders) {
            return PickupTimesList$;
        } else {
            return PickupTimesList$.pipe(
                map(pickupTimes => {
                    if (pickupTimes === null) return null;
                    const ASAP = [...pickupTimes].slice(-1)[0];
                    const pickupTimesFiltered = [...pickupTimes];
                    pickupTimesFiltered.pop();

                    return [...pickupTimesFiltered
                        .filter(time => time.IsAsap || time.Id < 0 || (new Date(time.DateLocalISO).getTime()) > (new Date()).getTime())
                        .slice(0, 3), ASAP];
                }));
        }
    }
}
